<template>
  <div class="badge-prep pa-10">
    <title-page>{{ $t('onboarding.badge-prep.title') }}</title-page>

    <v-btn small text class="mb-2 px-0" :to="{ name: 'onboarding.commissionings', query: { queue: 'shipment' } }">
      <v-icon left small>mdi-arrow-left</v-icon>{{ $t('onboarding.queue.step.shipment') }}
    </v-btn>

    <v-alert v-if="error" type="error" dense outlined class="mb-4">{{ error }}</v-alert>
    <v-skeleton-loader v-if="loading && !order" type="card, card" />

    <template v-else-if="order">
      <!-- Où en est l'envoi : vérifier, choisir les badges, générer le courrier, poster -->
      <v-card class="mb-4">
        <v-card-text>
          <v-stepper :value="stepIndex" alt-labels flat class="badge-prep__stepper">
            <v-stepper-header>
              <template v-for="(step, index) in STEPS">
                <v-stepper-step :key="step" :step="index + 1" :complete="stepIndex > index + 1">{{ $t(`onboarding.badge-prep.step.${step}`) }}</v-stepper-step>
                <v-divider v-if="index < STEPS.length - 1" :key="`${step}-divider`" />
              </template>
            </v-stepper-header>
          </v-stepper>
          <v-alert v-if="shipped" type="success" dense text class="mb-0 mt-2">
            {{ $t('onboarding.badge-prep.shipped', { date: $options.filters.formatDate(shippedAt) }) }}
          </v-alert>
        </v-card-text>
      </v-card>

      <v-row>
        <v-col cols="12" lg="5">
          <!-- Le destinataire : qui, pour quelle organisation, comment le joindre -->
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.badge-prep.recipient') }}</v-card-title>
            <v-card-text>
              <div class="text-h6 font-weight-regular">{{ personName(order) }}</div>
              <div class="text-body-2">
                <router-link target="_blank" :to="{ name: 'organizations.detail', params: { id: order.organizationId } }">{{ order.organizationName }}</router-link>
                <template v-if="order.memberId"> · <router-link target="_blank" :to="{ name: 'member', params: { memberId: order.memberId } }">{{ $t('onboarding.badge-prep.member') }}</router-link></template>
                <template v-if="registrationId"> · <router-link target="_blank" :to="{ name: 'onboarding.registrations.detail', params: { id: registrationId } }">{{ $t('onboarding.commissioning.see-registration') }}</router-link></template>
              </div>
              <div v-if="order.person && order.person.email" class="text-body-2 mt-2"><v-icon small class="mr-1">mdi-email-outline</v-icon>{{ order.person.email }}</div>
              <div v-if="order.person && order.person.phone" class="text-body-2"><v-icon small class="mr-1">mdi-phone-outline</v-icon>{{ order.person.phone }}</div>
              <div class="text-caption text--secondary mt-2">{{ $t('onboarding.badge-shipments.requested') }} {{ order.createdAt | formatDate }}</div>
            </v-card-text>
          </v-card>

          <!-- L'adresse, telle qu'elle sera sur l'enveloppe ; corrigeable jusqu'au départ -->
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">
              {{ $t('onboarding.badge-prep.address') }}
              <v-spacer />
              <v-btn v-if="!shipped && !editingAddress" small text color="primary" @click="editAddress"><v-icon left small>mdi-pencil</v-icon>{{ $t('edit') }}</v-btn>
            </v-card-title>
            <v-card-text>
              <v-chip v-if="order.shippingAddressChangedAt" x-small color="orange" text-color="white" class="mb-2">
                {{ $t('onboarding.badge-shipments.address-changed', { date: $options.filters.formatDate(order.shippingAddressChangedAt) }) }}
              </v-chip>
              <div v-if="!editingAddress" class="badge-prep__envelope">
                <div class="font-weight-bold">{{ personName(order) }}</div>
                <div v-for="(line, i) in envelopeLines" :key="i">{{ line }}</div>
                <div v-if="!envelopeLines.length" class="error--text">{{ $t('onboarding.badge-prep.no-address') }}</div>
              </div>
              <template v-else>
                <address-autocomplete :value="null" :label="$t('onboarding.badge-prep.search-address')" @input="pickAddress" />
                <v-row dense>
                  <v-col cols="3"><v-text-field v-model="address.number" :label="$t('onboarding.badge-prep.field.number')" outlined dense hide-details /></v-col>
                  <v-col cols="9"><v-text-field v-model="address.street" :label="$t('onboarding.badge-prep.field.street')" outlined dense hide-details /></v-col>
                  <v-col cols="12"><v-text-field v-model="address.additionalInformation" :label="$t('onboarding.badge-prep.complement')" outlined dense hide-details /></v-col>
                  <v-col cols="4"><v-text-field v-model="address.zipcode" :label="$t('onboarding.badge-prep.field.zipcode')" outlined dense hide-details /></v-col>
                  <v-col cols="8"><v-text-field v-model="address.city" :label="$t('onboarding.badge-prep.field.city')" outlined dense hide-details /></v-col>
                </v-row>
                <p v-if="order.preparedAt" class="text-caption mt-2 mb-0">{{ $t('onboarding.badge-prep.reprint') }}</p>
                <div class="d-flex justify-end mt-3" style="gap: 8px">
                  <v-btn small text @click="editingAddress = false">{{ $t('cancel') }}</v-btn>
                  <v-btn small color="primary" :loading="working === 'address'" :disabled="!addressComplete" @click="saveAddress">{{ $t('save') }}</v-btn>
                </div>
              </template>
            </v-card-text>
          </v-card>

          <!-- Les badges qui partent : lesquels, et leur numéro -->
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">{{ $t('onboarding.badge-prep.badges') }}</v-card-title>
            <v-card-text>
              <p class="text-caption text--secondary">{{ $t('onboarding.badge-prep.badges-explained', { ordered: supplyLabel }) }}</p>
              <!-- Tous les badges commandés partent ensemble (le serveur l'exige) : la liste se lit, elle ne se choisit pas. -->
              <div class="d-flex mb-3" style="gap: 8px">
                <v-chip v-for="kind in orderedKinds" :key="kind" small outlined>
                  <v-icon x-small left>mdi-card-account-details-outline</v-icon>{{ $t(`onboarding.commissioning.badge-supply.${kind}`) }}
                </v-chip>
              </div>
              <!-- Noter un badge est facultatif (décision du 18/10/2026) : noté, il est vérifié, imprimé sur le courrier et sort
                   du stock ; sinon on glisse un badge du stock et la personne l'active en le recevant -->
              <badge-entry
                  v-for="kind in selected" :key="kind" :order-id="order.id" :kind="kind"
                  :label="$t(`onboarding.commissioning.badge-supply.${kind}`)" :held="held[kind] || ''"
                  :held-by="order.badgesHeldBy" :held-at="order.badgesHeldAt" :readonly="prepared" class="mb-4"
                  @held="onHeld" @dirty="dirty = { ...dirty, [kind]: $event }" @verdict="verdicts = { ...verdicts, [kind]: $event }"
              />
              <p v-if="!selected.length" class="text-caption error--text mb-0">{{ $t('onboarding.badge-prep.none-selected') }}</p>
              <v-alert v-else-if="!prepared && missing.length" type="info" text dense class="text-body-2 mb-0">
                {{ $t('onboarding.badge-prep.not-noted', { badges: missing.map(k => $t(`onboarding.commissioning.badge-supply.${k}`)).join(', ') }) }}
              </v-alert>
            </v-card-text>
            <!-- Le traitement : valider l'envoi, après un récapitulatif à confirmer. Rien n'est rattaché avant. -->
            <v-divider />
            <v-card-actions class="pa-4">
              <v-btn v-if="order.status === 'open'" small text color="error" @click="$router.push({ name: 'onboarding.commissionings.detail', params: { id: order.id } })">
                {{ $t('onboarding.badge-prep.cancel-or-reject') }}
              </v-btn>
              <v-spacer />
              <v-btn v-if="!prepared" small color="primary" :disabled="!readyToValidate" @click="recap = true">
                <v-icon left small>mdi-check-decagram-outline</v-icon>{{ $t('onboarding.badge-prep.validate') }}
              </v-btn>
              <v-btn v-else small outlined :loading="working === 'letter'" @click="printLetter"><v-icon left small>mdi-printer</v-icon>{{ $t('onboarding.badge-prep.print') }}</v-btn>
            </v-card-actions>
          </v-card>
        </v-col>

        <!-- Le courrier : son aperçu, puis le vrai, à imprimer -->
        <v-col cols="12" lg="7">
          <v-card class="mb-4">
            <v-card-title class="text-subtitle-1">
              {{ $t('onboarding.badge-prep.letter') }}
              <v-chip v-if="prepared" x-small color="green" text-color="white" class="ml-2">{{ $t('onboarding.badge-prep.generated') }}</v-chip>
              <v-chip v-else x-small outlined class="ml-2">{{ $t('onboarding.badge-prep.preview') }}</v-chip>
              <v-spacer />
              <v-btn v-if="!prepared" small text :loading="previewing" @click="refreshPreview"><v-icon left small>mdi-refresh</v-icon>{{ $t('onboarding.badge-prep.refresh') }}</v-btn>
            </v-card-title>
            <v-card-text>
              <div class="badge-prep__letter">
                <iframe v-if="letterUrl" :src="letterUrl" :title="$t('onboarding.badge-prep.letter')" />
                <div v-else class="badge-prep__letter-empty text--secondary">{{ previewing ? $t('onboarding.badge-prep.loading') : $t('onboarding.badge-prep.no-preview') }}</div>
              </div>
            </v-card-text>
          </v-card>
        </v-col>
      </v-row>
    </template>

    <!-- Le récapitulatif : qui, où, quels badges ; ce qui est déjà à quelqu'un d'autre est dit ; puis on valide -->
    <v-dialog v-model="recap" max-width="600">
      <v-card v-if="order">
        <v-card-title class="text-h6">{{ $t('onboarding.badge-prep.recap-title') }}</v-card-title>
        <v-card-text>
          <v-simple-table dense class="mb-3">
            <tbody>
              <tr><th>{{ $t('onboarding.badge-prep.recipient') }}</th><td>{{ personName(order) }} · {{ order.organizationName }}</td></tr>
              <tr><th>{{ $t('onboarding.badge-prep.address') }}</th><td>{{ envelopeLines.join(', ') }}</td></tr>
              <tr v-for="kind in selected" :key="kind">
                <th>{{ $t(`onboarding.commissioning.badge-supply.${kind}`) }}</th>
                <td v-if="(held[kind] || '').trim()">{{ held[kind] }} · {{ verdicts[kind] ? $t(`onboarding.badge-prep.recap-status.${verdicts[kind].status}`) : '' }}</td>
                <td v-else class="text--secondary">{{ $t('onboarding.badge-prep.recap-not-noted') }}</td>
              </tr>
            </tbody>
          </v-simple-table>
          <v-alert v-for="warning in warnings" :key="warning.kind" type="warning" text dense class="text-body-2">{{ warning.text }}</v-alert>
          <v-alert type="info" text dense class="text-body-2 mb-0">{{ $t('onboarding.badge-prep.recap-inactive') }}</v-alert>
        </v-card-text>
        <v-card-actions>
          <v-spacer />
          <v-btn small text @click="recap = false">{{ $t('back') }}</v-btn>
          <v-btn small color="primary" :loading="working === 'ship'" @click="validate">{{ $t('onboarding.badge-prep.validate') }}</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </div>
</template>

<script>
import CommissioningRepository from '@repository/CommissioningRepository'
import CommissioningMixin from './CommissioningMixin'
import AddressAutocomplete from './AddressAutocomplete.vue'
import BadgeEntry from './Commissioning/BadgeEntry.vue'

const STEPS = ['check', 'badges', 'validate', 'activation']

/**
 * Préparer l'envoi d'un badge, comme on prépare un colis : le destinataire et son adresse (corrigeable), les badges qui
 * partent — pro, perso, ou les deux, avec le numéro de ceux qu'on a en main —, l'aperçu du courrier qui suit ce choix
 * (un emplacement à coller par badge), puis le vrai courrier à imprimer, et l'envoi. Propre aux badges : une borne ne se
 * prépare pas ainsi.
 *
 * Chaque badge se valide un à un (« Valider ce badge ») et s'enregistre sur la commande : on peut s'arrêter, la saisie
 * reste — elle se voit dans la file (« saisie commencée »). Une frappe non validée est signalée avant de quitter, et
 * l'envoi ne se valide que lorsque tous les badges choisis sont enregistrés.
 */
export default {
  name: 'BadgeShipmentPreparation',

  components: { AddressAutocomplete, BadgeEntry },

  mixins: [CommissioningMixin],

  data() {
    return {
      STEPS,
      order: null,
      loading: false,
      error: null,
      working: null,
      selected: [],
      verdicts: {},
      dirty: {},
      recap: false,
      editingAddress: false,
      address: {},
      letterUrl: null,
      previewing: false,
      previewTimer: null,
    }
  },

  computed: {
    shippedAt() {
      return (this.order?.requirements || []).find(r => r.requirement === 'shipment')?.satisfiedAt || null
    },
    shipped() {
      return !!this.shippedAt
    },
    prepared() {
      return !!this.order?.preparedAt
    },
    stepIndex() {
      if (this.order?.status === 'in-service') return 5
      if (this.shipped) return 4
      if (this.selected.length) return 3
      return this.editingAddress ? 1 : 2
    },
    /** Les badges que la commande fournit. */
    orderedKinds() {
      return this.order?.orderedBadgeKinds?.length ? this.order.orderedBadgeKinds : [this.order?.options?.supply || 'professional']
    },
    /** Ce qui est enregistré sur la commande, badge par badge. */
    held() {
      return this.order?.designatedBadges || {}
    },
    /** Les badges choisis pas encore validés. */
    missing() {
      return this.selected.filter(k => !(this.held[k] || '').trim())
    },
    /** Les badges choisis dont le numéro est noté. */
    noted() {
      return this.selected.filter(k => (this.held[k] || '').trim())
    },
    anyDirty() {
      return Object.values(this.dirty).some(Boolean)
    },
    registrationId() {
      const ref = this.order?.originReference || ''
      return ref.startsWith('registration:') ? ref.slice('registration:'.length) : null
    },
    envelopeLines() {
      const a = this.order?.shippingAddress
      if (!a || !a.street) return []
      return [
        [a.number, a.street].filter(Boolean).join(' '),
        a.additionalInformation,
        [a.zipcode, a.city].filter(Boolean).join(' '),
        a.country && a.country !== 'FR' ? a.country : null,
      ].filter(Boolean)
    },
    supplyLabel() {
      const key = `onboarding.commissioning.badge-supply.${this.order?.options?.supply}`
      return this.$te(key) ? this.$t(key).toLowerCase() : ''
    },
    addressComplete() {
      return !!(this.address.street || '').trim() && !!(this.address.zipcode || '').trim() && !!(this.address.city || '').trim()
    },
    /** Une adresse, des badges choisis, chacun enregistré et encore prenable ; aucune frappe en suspens. */
    readyToValidate() {
      // Les badges notés doivent être bons ; ceux qui ne le sont pas partent sans numéro.
      return this.envelopeLines.length > 0 && this.selected.length > 0 && !this.anyDirty
        && this.noted.every(k => this.verdicts[k] && (this.verdicts[k].status === 'free' || this.verdicts[k].needsConfirmation))
    },
    warnings() {
      return this.selected.filter(k => this.verdicts[k]?.needsConfirmation).map(kind => {
        const v = this.verdicts[kind]
        return {
          kind,
          text: this.$t(`onboarding.badge-check.warning.${v.status}`, { badge: this.held[kind], organization: v.organizationName || '-' }),
        }
      })
    },
    /** Ce qui part (pour l'aperçu) : les badges choisis, avec leur numéro s'il est enregistré. */
    badges() {
      return {
        professional: this.selected.includes('professional') ? (this.held.professional || '') : null,
        personal: this.selected.includes('personal') ? (this.held.personal || '') : null,
      }
    },
  },

  watch: {
    /** Une frappe non validée : l'application la signale avant de changer de page, le navigateur avant de fermer. */
    anyDirty(dirty) {
      dirty ? this.setUnsavedChanges() : this.cleanUnsavedChanges()
    },
  },

  created() {
    this.load()
    window.addEventListener('beforeunload', this.warnBeforeUnload)
  },

  beforeDestroy() {
    window.removeEventListener('beforeunload', this.warnBeforeUnload)
    this.cleanUnsavedChanges()
    if (this.letterUrl && this.letterUrl.startsWith('blob:')) URL.revokeObjectURL(this.letterUrl)
  },

  methods: {
    async load() {
      this.loading = true
      this.error = null
      try {
        this.order = await CommissioningRepository.get(this.$route.params.id)
        const designated = Object.keys(this.order.designatedBadges || {})
        this.selected = this.prepared && designated.length ? designated : [...this.orderedKinds]
        if (this.prepared) await this.showPrintedLetter()
        else await this.refreshPreview()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.loading = false
      }
    },

    warnBeforeUnload(event) {
      if (!this.anyDirty) return
      event.preventDefault()
      event.returnValue = ''
    },

    /** Un badge enregistré (ou retiré) : la commande à jour, et l'aperçu avec le bon numéro. */
    onHeld({ order }) {
      this.order = order
      this.schedulePreview()
    },

    schedulePreview() {
      clearTimeout(this.previewTimer)
      this.previewTimer = setTimeout(this.refreshPreview, 300)
    },

    setLetter(url) {
      if (this.letterUrl && this.letterUrl.startsWith('blob:')) URL.revokeObjectURL(this.letterUrl)
      this.letterUrl = url
    },

    async refreshPreview() {
      if (this.prepared || !this.selected.length) {
        if (!this.prepared) this.setLetter(null)
        return
      }
      this.previewing = true
      try {
        this.setLetter(await CommissioningRepository.previewBadgeLetter(this.order.id, this.badges))
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.previewing = false
      }
    },

    async showPrintedLetter() {
      try {
        this.setLetter(await CommissioningRepository.badgeLetterFile(this.order.id))
      } catch (e) {
        this.setLetter(null)
      }
    },

    editAddress() {
      this.address = { number: '', street: '', additionalInformation: '', zipcode: '', city: '', country: 'FR', ...(this.order.shippingAddress || {}) }
      this.editingAddress = true
    },

    pickAddress(picked) {
      if (picked) this.address = { ...this.address, ...picked, additionalInformation: picked.additionalInformation || this.address.additionalInformation }
    },

    async saveAddress() {
      await this.act('address', async () => {
        const { coordinates, ...address } = this.address // eslint-disable-line no-unused-vars
        this.order = await CommissioningRepository.changeShippingAddress(this.order.id, address)
        this.editingAddress = false
        if (this.prepared) await this.showPrintedLetter()
        else await this.refreshPreview()
      })
    },

    /** L'envoi validé : les badges rattachés inactifs, le courrier imprimé — on l'affiche, prêt à imprimer. */
    async validate() {
      await this.act('ship', async () => {
        // Ce qui part est ce qui est enregistré sur la commande.
        await CommissioningRepository.shipBadgeOrder(this.order.id, { confirmed: this.warnings.length > 0 })
        this.recap = false
        this.order = await CommissioningRepository.get(this.order.id)
        await this.showPrintedLetter()
      })
    },

    /** Le courrier dans un onglet à lui, prêt pour l'impression du navigateur. */
    async printLetter() {
      await this.act('letter', async () => {
        if (!this.letterUrl) await this.showPrintedLetter()
        window.open(this.letterUrl, '_blank')
      })
    },


    async act(name, call) {
      this.working = name
      this.error = null
      try {
        await call()
      } catch (e) {
        this.error = this.messageOf(e)
      } finally {
        this.working = null
      }
    },
  },
}
</script>

<style scoped>
.badge-prep__stepper {
  box-shadow: none;
}
.badge-prep__envelope {
  padding: 16px 20px;
  border: 1px dashed rgba(0, 0, 0, 0.25);
  border-radius: 6px;
  background: #fffdf7;
  line-height: 1.6;
}
.badge-prep__letter {
  height: 760px;
  border: 1px solid rgba(0, 0, 0, 0.1);
  border-radius: 6px;
  overflow: hidden;
  background: #f5f5f5;
}
.badge-prep__letter iframe {
  width: 100%;
  height: 100%;
  border: 0;
}
.badge-prep__letter-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 100%;
}
</style>
